'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { useLocaleRouter } from '@/hooks/useLocaleRouter';
import JourneyContactModal from '@/components/JourneyContactModal';
import CollapsibleJourneyFilters from '@/components/CollapsibleJourneyFilters';
import WishlistButton from '@/components/WishlistButton';
import { getCategoryById, JOURNEY_CATEGORIES, AGE_CATEGORY_IDS } from '@/lib/categories';
import { Title, Text } from '@/components/ui/Headings';

interface Journey {
  id: number;
  title: { en: string; de: string; uk: string };
  subtitle: { en: string; de: string; uk: string };
  description: { en: string; de: string; uk: string };
  country: { en: string; de: string; uk: string } | null;
  highlights: { en: string[]; de: string[]; uk: string[] };
  overview: { en: string; de: string; uk: string } | null;
  whatMakesItSpecial: { en: string; de: string; uk: string } | null;
  itinerary: {
    en: Array<{day: number; title: string; description: string}>;
    de: Array<{day: number; title: string; description: string}>;
    uk: Array<{day: number; title: string; description: string}>;
  } | null;
  includes: { en: string[]; de: string[]; uk: string[] };
  notIncluded: { en: string[]; de: string[]; uk: string[] };
  categories: string[];
  availableDates: Array<{month: string; status: string; price: string}> | null;
  image: string;
  duration: string;
  groupSize: string;
  startingPrice: string;
  currency: string | null;
  isActive: boolean;
  displayOrder: number | null;
  // Optional metadata fields (not returned by public API to avoid SSR serialization issues)
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string | null;
}

interface SignatureJourneysClientProps {
  journeys: Journey[];
}

export default function SignatureJourneysClient({ journeys }: SignatureJourneysClientProps) {
  const { i18n } = useTranslation();
  const { createLocalizedPath } = useLocaleRouter();
  
  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    journeyId: number;
    journeyTitle: string;
  }>({
    isOpen: false,
    journeyId: 0,
    journeyTitle: ''
  });

  // Filter state
  const [filters, setFilters] = useState({
    countries: [] as string[],
    duration: 'all',
    durationMin: '1',
    durationMax: '30',
    months: [] as string[],
    ages: [] as string[],
    themes: [] as string[]
  });

  // Get current language
  const currentLang = (i18n.language || 'en') as 'en' | 'de' | 'uk';

  // Format price with currency
  const formatPrice = (price: string | null | undefined, currency: string | null | undefined) => {
    const currencySymbols: { [key: string]: string } = {
      'EUR': '€',
      'USD': '$',
      'GBP': '£',
      'JPY': '¥',
      'CAD': 'C$',
      'AUD': 'A$',
      'CHF': 'CHF',
      'NOK': 'kr',
      'SEK': 'kr',
      'DKK': 'kr'
    };
    
    const currencyCode = currency || 'EUR';
    const symbol = currencySymbols[currencyCode] || '€';

    if (!price) {
      return `${symbol}—`;
    }

    const cleanPrice = price.replace(/[€$£¥]|C\$|A\$|CHF|kr/g, '').replace(/,/g, '').trim();
    const numericPrice = parseFloat(cleanPrice);
    const formattedPrice = Number.isFinite(numericPrice) ? numericPrice.toLocaleString() : price;
    return `${symbol}${formattedPrice}`;
  };

  // Extract unique values for filters
  const filterOptions = useMemo(() => {
    // Extract individual countries from all journey countries
    const allCountries = new Set<string>();
    
    journeys.forEach(journey => {
      const countryText = journey.country?.[currentLang] || journey.country?.en;
      if (countryText) {
        // Split by common separators and clean up
        const countries = countryText
          .split(/[,&]/) // Split by comma or ampersand
          .map(country => country.trim())
          .filter(country => country.length > 0 && country !== 'and' && country !== '&');
        
        countries.forEach(country => allCountries.add(country));
      }
    });
    
    const countries = Array.from(allCountries).sort();

    const durations = Array.from(new Set(
      journeys.map(journey => journey.duration)
    )).sort((a, b) => {
      // Extract numeric part for sorting
      const aNum = parseInt(a.replace(/\D/g, ''));
      const bNum = parseInt(b.replace(/\D/g, ''));
      return aNum - bNum;
    });

    // Extract months without years and map to simple month names
    const monthMap: { [key: string]: string } = {
      'January': 'January', 'February': 'February', 'March': 'March', 'April': 'April',
      'May': 'May', 'June': 'June', 'July': 'July', 'August': 'August',
      'September': 'September', 'October': 'October', 'November': 'November', 'December': 'December'
    };
    
    const months = Array.from(new Set(
      journeys
        .flatMap(journey => journey.availableDates || [])
        .map(date => {
          // Extract just the month name from strings like "April 2024" or "January 2025"
          const monthName = date.month.split(' ')[0];
          return monthMap[monthName] || monthName;
        })
        .filter(Boolean)
    )).sort((a, b) => {
      // Sort by month order (January to December)
      const monthOrder: { [key: string]: number } = {
        'January': 1, 'February': 2, 'March': 3, 'April': 4,
        'May': 5, 'June': 6, 'July': 7, 'August': 8,
        'September': 9, 'October': 10, 'November': 11, 'December': 12
      };
      return (monthOrder[a] || 99) - (monthOrder[b] || 99);
    });

    // Get unique category IDs from all journeys
    const uniqueCategoryIds = Array.from(new Set(
      journeys.flatMap(journey => journey.categories)
    ));
    
    // Separate age categories from theme categories
    const themeCategoryIds = uniqueCategoryIds.filter(id => !AGE_CATEGORY_IDS.includes(id));
    
    // Build age groups: show all age categories from the system
    const ageCategoryLabels = AGE_CATEGORY_IDS
      .map(categoryId => {
        const category = getCategoryById(categoryId);
        return category ? category.label : null;
      })
      .filter(Boolean) as string[];
    
    const ageGroups = ageCategoryLabels;
    
    // Build themes: only non-age categories
    const themes = themeCategoryIds
      .map(categoryId => {
        const category = getCategoryById(categoryId);
        return category ? category.label : categoryId;
      })
      .sort();

    return { countries, durations, months, ageGroups, themes };
  }, [journeys, currentLang]);

  // Filter journeys based on selected filters
  const filteredJourneys = useMemo(() => {
    return journeys.filter(journey => {
      const journeyCategories = Array.isArray(journey.categories) ? journey.categories : [];
      const groupSizeValue = journey.groupSize?.toLowerCase?.() ?? '';

      // Country filter - check if journey matches ANY selected country
      if (filters.countries.length > 0) {
        const journeyCountry = journey.country?.[currentLang] || journey.country?.en;
        if (!journeyCountry) return false;
        
        const matchesCountry = filters.countries.some(selectedCountry => 
          journeyCountry.toLowerCase().includes(selectedCountry.toLowerCase())
        );
        if (!matchesCountry) return false;
      }

      // Duration filter - range support
      if (filters.duration !== 'all') {
        const journeyDays = parseInt(journey.duration.replace(/\D/g, ''), 10);
        if (isNaN(journeyDays)) return true;
        
        const minDays = parseInt(filters.durationMin || '1', 10);
        const maxDays = parseInt(filters.durationMax || '30', 10);
        
        if (journeyDays < minDays || journeyDays > maxDays) return false;
      }

      // Month filter - check if journey matches ANY selected month
      if (filters.months.length > 0) {
        const hasMatchingMonth = journey.availableDates?.some(date => {
          const dateMonth = date.month.split(' ')[0];
          return filters.months.includes(dateMonth);
        });
        if (!hasMatchingMonth) return false;
      }

      // Age filter - check if journey matches ANY selected age group
      if (filters.ages.length > 0) {
        const categories = journeyCategories.map(cat => cat.toLowerCase());
        
        const matchesAge = filters.ages.some(selectedAge => {
          // First check if it's a category-based age filter
          const ageCategory = JOURNEY_CATEGORIES.find(cat => 
            cat.label === selectedAge && AGE_CATEGORY_IDS.includes(cat.id)
          );
          
          if (ageCategory) {
            // Filter by category ID
            return journeyCategories.includes(ageCategory.id);
          } else {
            // Handle hardcoded age groups
            switch (selectedAge) {
              case 'Adults Only':
                return groupSizeValue.includes('adult') || categories.some(cat => cat.includes('adult'));
              case 'Family Friendly':
                return groupSizeValue.includes('family') || categories.some(cat => cat.includes('family'));
              case 'Senior Friendly':
                return groupSizeValue.includes('senior') || categories.some(cat => cat.includes('senior'));
              default:
                return false;
            }
          }
        });
        
        if (!matchesAge) return false;
      }

      // Theme filter - check if journey matches ANY selected theme
      if (filters.themes.length > 0) {
        const matchesTheme = filters.themes.some(selectedTheme => {
          // Find the category ID from the label (excluding age categories)
          const category = JOURNEY_CATEGORIES.find(cat => 
            cat.label === selectedTheme && !AGE_CATEGORY_IDS.includes(cat.id)
          );
          const categoryId = category ? category.id : selectedTheme;
          return journeyCategories.includes(categoryId);
        });
        
        if (!matchesTheme) return false;
      }

      return true;
    });
  }, [journeys, filters, currentLang]);

  // Modal handlers
  const openModal = (journeyId: number, journeyTitle: string) => {
    setModalState({
      isOpen: true,
      journeyId,
      journeyTitle
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      journeyId: 0,
      journeyTitle: ''
    });
  };


  return (
    <div>
      {/* Inline Filter Component */}
      <CollapsibleJourneyFilters
        filters={filters}
        setFilters={setFilters}
        filterOptions={filterOptions}
        filteredCount={filteredJourneys.length}
        totalCount={journeys.length}
      />

      {/* Main Content Area */}
      <div>
          {filteredJourneys.length === 0 ? (
          <div className="text-center py-12">
            <Title size="sm" className="font-medium mb-2">No journeys found</Title>
            <Text variant="body" className="text-gray-600 mb-4">Try adjusting your filters to see more results.</Text>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6">
            {filteredJourneys.map((journey) => {
              // Get translated content for current language
              const title = journey.title[currentLang] || journey.title.en;
              const country = journey.country?.[currentLang] || journey.country?.en;
              const imageSrc = journey.image && journey.image.trim().length > 0
                ? journey.image
                : "https://images.pexels.com/photos/3278215/pexels-photo-3278215.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=600";

              return (
                <div
                  key={journey.id}
                  className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col h-full"
                >
                  <div className="relative">
                    <Link
                      href={createLocalizedPath(`/signature-journeys/${journey.id}`)}
                      className="block"
                    >
                      <div className="relative overflow-hidden h-64">
                        <Image
                          src={imageSrc}
                          alt={title}
                          fill
                          className="object-cover transform group-hover:scale-105 transition-transform duration-500"
                        />
                        {/* Country Badge */}
                        {country && (
                          <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-gray-800">
                            {country}
                          </div>
                        )}
                      </div>
                    </Link>
                    
                    {/* Wishlist Button - Top Left Corner */}
                    <div className="absolute top-3 left-3 z-10">
                      <WishlistButton journeyId={journey.id} size="md" />
                    </div>
                  </div>

                  <div className="px-6 pt-3 pb-6 flex flex-col flex-grow">
                    {/* Journey Info - Duration and Price in one line */}
                    <div className="flex items-center justify-between mb-1 text-sm">
                      <div className="flex items-center space-x-4 text-gray-600 min-w-0 flex-1">
                        <Link
                          href={createLocalizedPath(`/signature-journeys/${journey.id}`)}
                          className="flex items-center space-x-2 flex-shrink-0 hover:text-primary-600 transition-colors cursor-pointer"
                        >
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium">{journey.duration}</span>
                        </Link>
                      </div>
                      <Link
                        href={createLocalizedPath(`/signature-journeys/${journey.id}`)}
                        className="text-lg font-bold text-primary-600 flex-shrink-0 ml-4 hover:text-primary-700 transition-colors cursor-pointer"
                      >
                        {formatPrice(journey.startingPrice, journey.currency)}
                      </Link>
                    </div>

                    <Link
                      href={createLocalizedPath(`/signature-journeys/${journey.id}`)}
                      className="block hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      <Title size="sm" className="font-semibold mb-4 group-hover:text-primary-900 transition-colors line-clamp-2 min-h-[1.5rem]">
                        {title}
                      </Title>
                    </Link>
                    
                    {/* Highlights removed per design */}

                    {/* CTA Button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openModal(journey.id, title);
                      }}
                      className="mt-auto text-primary-600 text-xs font-semibold transition-colors hover:text-primary-700 hover:underline self-end focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                    >
                      Email Me Journey
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Journey Contact Modal */}
      <JourneyContactModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        journeyTitle={modalState.journeyTitle}
        journeyId={modalState.journeyId}
      />
    </div>
  );
}
